import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import Header from './components/Header';
import ExpenseList from './components/ExpenseList';
import ExpenseListView from './components/ExpenseListView';
import EditModal from './components/EditModal';
import LoadModal from './components/LoadModal';
import ExportModal from './components/ExportModal';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import GigLandingPage from './components/GigLandingPage';
import ConsultantLandingPage from './components/ConsultantLandingPage';
import InfluencerLandingPage from './components/InfluencerLandingPage';
import PersonalFinanceLandingPage from './components/PersonalFinanceLandingPage';
import PricingPage from './components/PricingPage';
import NewReportModal from './components/NewReportModal';
import PrefsModal from './components/PrefsModal';
import ShareModal from './components/ShareModal';
import UpgradePrompt from './components/UpgradePrompt';
import AdminPanel from './components/admin/AdminPanel';
import BillingModal from './components/BillingModal';
import CreateReviewLinkModal from './components/CreateReviewLinkModal';
import ReviewLinksModal from './components/ReviewLinksModal';
import ReviewPage from './components/ReviewPage';
import CreditCardImportModal from './components/CreditCardImportModal';
import BankImportModal from './components/BankImportModal';
import BrokerageImportModal from './components/BrokerageImportModal';
import BusinessSummaryView from './components/BusinessSummaryView';
import TransactionAccountView from './components/TransactionAccountView';
import FinancialSummaryView from './components/FinancialSummaryView';
import TaxSummaryView from './components/TaxSummaryView';
import AddExpenseView from './components/AddExpenseView';
import DashboardPage from './components/DashboardPage';
import BookkeeperPanel from './components/BookkeeperPanel';
import { useExpenseData } from './hooks/useExpenseData';
import { useFileOperations } from './hooks/useFileOperations';
import { useCategories } from './hooks/useCategories';
import { usePresetViews } from './hooks/usePresetViews';
import { useAuth } from './contexts/AuthContext';
import { useCurrency } from './hooks/useCurrency';
import { useTier } from './hooks/useTier';
import { useUpgradePrompt } from './hooks/useUpgradePrompt';
import { loadExpenseFile, saveExpenseFile, saveFile, loadFile, saveTransactionAccount, listTransactionAccounts, ensureTransactionAccount, markItemsReimbursed } from './utils/firestoreStorage';
import ReimbursementModal from './components/ReimbursementModal';
import CreateAccountModal from './components/CreateAccountModal';
import { generateMcpToken, revokeMcpToken } from './utils/mcpStorage';
import GlobalFooterBar from './components/GlobalFooterBar';
import './utils/storage';
import { T } from './theme';
import HelpModal from './components/HelpModal';
import { trackAddExpense } from './analytics';
import { loadBkUsage } from './utils/bookkeeperApi';
import { autoClassifyFiles } from './utils/autoClassify';
import { findDuplicate } from './utils/csvParser';
import { resolveClassification } from './utils/constants';


function App() {
  const { user, logout, account, refreshAccount } = useAuth();
  const [skippedLogin, setSkippedLogin] = useState(false);
  const uid = user?.uid || null;
  const { tier, limits, tierConfig, scansUsed, totalScansAllowed, checkLimit, refreshUsage } = useTier();
  const { promptProps, showUpgradePrompt, closeUpgradePrompt } = useUpgradePrompt();

  const { expenses, addExpense, updateExpense, deleteExpense, totalSpend, setExpenses } = useExpenseData();
  const {
    defaultCurrency,
    adjustmentFactor,
    setPreferences,
    convertExpense,
    reconvertAllExpenses,
    ratesLoading,
    ratesError,
  } = useCurrency(uid);
  const {
    saveMsg,
    autoSaving,
    filesLoaded,
    lastFileLoaded,
    showLoadModal,
    setShowLoadModal,
    showExport,
    setShowExport,
    savedFiles,
    sharedWithMeFiles,
    currentFileName,
    setCurrentFileName,
    currentFileReportType,
    setCurrentFileReportType,
    currentFileOwnerUid,
    setCurrentFileOwnerUid,
    currentFileAccess,
    setCurrentFileAccess,
    currentFileLocal,
    handleLoad,
    handleLoadShared,
    handleDelete,
    loadSavedFiles,
    patchSavedFile,
    handleRestoreLocalFile,
  } = useFileOperations(expenses, setExpenses, uid, limits.imageStorage);

  const {
    allCategories, customCategories, addCategory, getTaxCategory,
    allIncomeCategories, addIncomeCategory,
  } = useCategories(uid);

  const { enabledViews: presetViews } = usePresetViews(uid);

  // 'landing' | 'login' | 'signup'  — drives pre-auth screen
  // __initialAuthScreen is set by index.js when a persona landing page navigates into the app.
  const initialScreen = window.__initialAuthScreen === 'skip'    ? 'landing'
                      : window.__initialAuthScreen === 'signup'  ? 'signup'
                      : window.__initialAuthScreen === 'login'   ? 'login'
                      : window.__initialAuthScreen === 'pricing' ? 'pricing'
                      : 'landing';
  if (window.__initialAuthScreen) { delete window.__initialAuthScreen; }
  const [authScreen, setAuthScreen] = useState(initialScreen);

  const [view, setView] = useState("home");
  const [addReturnView, setAddReturnView] = useState("home");
  const initialViewSet = useRef(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showNoReportModal, setShowNoReportModal] = useState(false);
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [showPrefsModal, setShowPrefsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingFile, setSharingFile] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showCreateReviewLink, setShowCreateReviewLink] = useState(false);
  const [showReviewLinks, setShowReviewLinks] = useState(false);
  const [showCreditCardImport, setShowCreditCardImport] = useState(false);
  const [showBankImport, setShowBankImport] = useState(false);
  const [showBrokerageImport, setShowBrokerageImport] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [transactionAccounts, setTransactionAccounts] = useState([]);
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [reimbursableResult, setReimbursableResult] = useState(null);
  const [pendingOwnerTxs, setPendingOwnerTxs] = useState([]);
  const [currentTransactionFile, setCurrentTransactionFile] = useState(null);
  const [bizAllLoaded, setBizAllLoaded] = useState({});
  const [bsvInitialFilters, setBsvInitialFilters] = useState(null);
  const [bizOpenReconcile, setBizOpenReconcile] = useState(false);
  const [bkMessages, setBkMessages] = useState([]);
  const [bkUsage, setBkUsage] = useState(null);
  const [showBookkeeper, setShowBookkeeper] = useState(false);
  const [dismissedGettingStarted, setDismissedGettingStarted] = useState(false);

  // Pre-load all transaction files on login so dashboardStats is populated before BSV mounts.
  // Uses a ref to track which names have been initiated (avoids re-fetching on every savedFiles change).
  const txLoadInitiatedRef = useRef(new Set());
  const prevUidForTxRef = useRef(null);
  useEffect(() => {
    if (!uid) return;
    if (uid !== prevUidForTxRef.current) {
      txLoadInitiatedRef.current = new Set();
      prevUidForTxRef.current = uid;
    }
    const toLoad = savedFiles.filter(
      f => (f.reportType === 'credit_card' || f.reportType === 'bank' || f.reportType === 'brokerage') && !txLoadInitiatedRef.current.has(f.name)
    );
    if (!toLoad.length) return;
    toLoad.forEach(f => txLoadInitiatedRef.current.add(f.name));
    Promise.all(toLoad.map(async f => {
      const file = await loadFile(uid, f.name, f.reportType);
      return file ? [f.name, file.data] : null;
    })).then(results => {
      const updates = Object.fromEntries(results.filter(Boolean));
      if (Object.keys(updates).length) setBizAllLoaded(prev => ({ ...prev, ...updates }));
    });
  }, [uid, savedFiles]);

  useEffect(() => {
    setBkMessages([]);
    setBkUsage(null);
    if (!uid) return;
    loadBkUsage(uid).then(usage => setBkUsage(usage)).catch(() => {});
  }, [uid]);

  useEffect(() => {
    if (!uid) { setTransactionAccounts([]); return; }
    listTransactionAccounts(uid).then(setTransactionAccounts).catch(() => {});
  }, [uid]);

  const dashboardStats = useMemo(() => {
    const expenseReports = savedFiles.filter(f => f.reportType === 'expense' || !f.reportType);
    const txFiles = savedFiles.filter(f => f.reportType === 'credit_card' || f.reportType === 'bank' || f.reportType === 'brokerage');
    const accountCount = new Set(txFiles.map(f => f.accountName || f.name)).size;
    // Only aggregate transaction file data — expense report items don't have businessClassification
    const txFileNames = new Set(txFiles.map(f => f.name));
    const allTx = Object.entries(bizAllLoaded)
      .filter(([name]) => txFileNames.has(name))
      .flatMap(([, data]) => data ?? []);

    let unreviewed = 0;
    let netBusinessIncome = 0, netBusinessExpenses = 0, netPersonalIncome = 0, netPersonalExpenses = 0;
    for (const tx of allTx) {
      const cls = resolveClassification(tx.businessClassification, tx.isRefund, tx.transactionDirection);
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (cls === 'unclassified' || cls === 'unclassified_debit' || cls === 'unclassified_credit') unreviewed++;
      if (cls === 'business_income')          netBusinessIncome   += amt;
      else if (cls === 'business_expense')    netBusinessExpenses += amt;
      else if (cls === 'business_expense_refund') netBusinessExpenses -= amt;
      else if (cls === 'personal_income')     netPersonalIncome   += amt;
      else if (cls === 'personal_expense')    netPersonalExpenses += amt;
      else if (cls === 'personal_expense_refund') netPersonalExpenses -= amt;
    }
    return {
      reportCount: expenseReports.length,
      accountCount,
      txCount: allTx.length,
      unreviewed,
      netBusinessIncome,
      netBusinessExpenses,
      netBusinessProfit: netBusinessIncome - netBusinessExpenses,
      netPersonalIncome,
      netPersonalExpenses,
      netPersonalProfit: netPersonalIncome - netPersonalExpenses,
      currentReport: currentFileName,
    };
  }, [savedFiles, bizAllLoaded]);
  // bulkCatOffer: shown after saving an expense whose category changed
  const [bulkCatOffer, setBulkCatOffer] = useState(null); // { vendor, category, peerIds[] }

  // Load saved files on mount
  useEffect(() => {
    loadSavedFiles();
  }, [loadSavedFiles]);

  // Reset reconcile-open flag whenever the user leaves the combined view.
  useEffect(() => {
    if (view !== 'combined') setBizOpenReconcile(false);
  }, [view]);

  // Browser back/forward support via History API.
  // Push a history entry whenever view or bsvInitialFilters changes.
  // On popstate (back button), restore view without pushing another entry.
  const isPopStateRef = useRef(false);
  const historyInitRef = useRef(false);
  useEffect(() => {
    if (isPopStateRef.current) { isPopStateRef.current = false; return; }
    const state = { view, bsvInitialFilters, currentTransactionFile };
    if (!historyInitRef.current) {
      historyInitRef.current = true;
      window.history.replaceState(state, ''); // replace the initial page-load entry
    } else {
      window.history.pushState(state, '');
    }
  }, [view, bsvInitialFilters, currentTransactionFile]);

  useEffect(() => {
    const onPopState = (e) => {
      if (!e.state?.view) return;
      isPopStateRef.current = true;
      setView(e.state.view);
      setBsvInitialFilters(e.state.bsvInitialFilters ?? null);
      setCurrentTransactionFile(e.state.currentTransactionFile ?? null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Capture PWA install prompt so we can trigger it from the UI.
  // Also check window.__pwaInstallPrompt in case the event fired before this
  // component mounted (captured early in index.js).
  useEffect(() => {
    if (window.__pwaInstallPrompt) setInstallPrompt(window.__pwaInstallPrompt);
    const onReady = () => { if (window.__pwaInstallPrompt) setInstallPrompt(window.__pwaInstallPrompt); };
    window.addEventListener('pwaInstallReady', onReady);
    return () => window.removeEventListener('pwaInstallReady', onReady);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect post-Stripe redirect and refresh account tier
  useEffect(() => {
    if (user && !initialViewSet.current) {
      initialViewSet.current = true;
      setView('dashboard');
    }
  }, [user]);



  const hasImportedTransactions = savedFiles.some(
    f => f.reportType === 'credit_card' || f.reportType === 'bank'
  );
  // hasChatted persists across sessions via localStorage so step 3 stays checked
  const hasChatted = bkUsage !== null || localStorage.getItem('myspend_gs_chatted') === '1';

  const gsAllDone = expenses.length > 0 && hasImportedTransactions && hasChatted;

  const handleDismissGettingStarted = () => setDismissedGettingStarted(true);

  // Reset per-session dismiss when uid changes (new login)
  useEffect(() => { setDismissedGettingStarted(false); }, [uid]);
  useEffect(() => { window.scrollTo(0, 0); }, [authScreen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      setUpgradeSuccess(true);
      setTimeout(() => setUpgradeSuccess(false), 5000);
      if (refreshAccount) refreshAccount();
    }
  }, [refreshAccount]);

  // Copy an expense into another saved file.
  // imageId records which expense owns the Storage object so the reconciler
  // can determine what's still referenced.
  const handleCopyExpense = useCallback(async (expense, targetFileName) => {
    if (!uid) return;
    const target = await loadExpenseFile(uid, targetFileName);
    const existingData = target?.data ?? [];
    const now = new Date().toISOString();
    const actor = user?.email ?? null;
    const copied = {
      ...expense,
      id: Date.now(),
      imageId: expense.imageId ?? (expense.imagePreview && !expense.imagePreview.startsWith('data:') ? expense.id : undefined),
      createdBy: actor,
      createdAt: now,
      lastModifiedBy: actor,
      lastModifiedAt: now,
    };
    await saveExpenseFile(uid, targetFileName, [copied, ...existingData]);
  }, [uid, user]);

  // Move an expense to another saved file.
  // The moved expense takes ownership of the Storage object (imageId is cleared
  // so its id becomes the canonical Storage key), and the source expense is removed.
  const handleMoveExpense = useCallback(async (expense, targetFileName) => {
    if (!uid) return;
    const target = await loadExpenseFile(uid, targetFileName);
    const existingData = target?.data ?? [];
    // Moved expense keeps the original id so the Storage path stays intact
    const moved = { ...expense, imageId: undefined };
    await saveExpenseFile(uid, targetFileName, [moved, ...existingData]);
    deleteExpense(expense.id);
    setEditingExpense(null);
  }, [uid, deleteExpense]);


  // If a ?review= or ?page= link was opened while the app is already running (e.g. PWA resume),
  // index.js won't re-run, so we catch it here as a fallback.
  const _params = new URLSearchParams(window.location.search);
  const reviewLinkId = _params.get('review');
  if (reviewLinkId) {
    return <ReviewPage linkId={reviewLinkId} />;
  }
  const _personaPages = {  gig: GigLandingPage, consultant: ConsultantLandingPage, influencer: InfluencerLandingPage, personal: PersonalFinanceLandingPage };
  const _PersonaPage = _personaPages[_params.get('page')];
  if (_PersonaPage) {
    window.history.replaceState({}, '', window.location.pathname);
    return <_PersonaPage
      onSignUp={() => setAuthScreen('signup')}
      onSignIn={() => setAuthScreen('login')}
      onSkip={() => { setSkippedLogin(true); setView('dashboard'); }}
      onPricing={() => setAuthScreen('pricing')}
    />;
  }

  // Show login page if auth state is loading
  if (user === undefined) {
    return (
      <div style={{
        minHeight: '100vh',
        background: T.gradNavy,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: T.fontBody,
      }}>
        <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>Loading...</div>
      </div>
    );
  }
  if (authScreen === 'pricing') {
      return (
        <PricingPage
          onBack={() => setAuthScreen('landing')}
          onSignUp={() => setAuthScreen('signup')}
          onSignIn={() => setAuthScreen('login')}
        />
      );
  }
  // Handle authScreen === 'home' for all users (logged in or not)
  if (authScreen === 'home' && user ) {
    return (
      <LandingPage
        user={user}
        onLogout={() => {
          setAuthScreen('landing');
          setSkippedLogin(false);
        }}
        onSignIn={() => setAuthScreen('login')}
        onSignUp={() => setAuthScreen('signup')}
        onSkip={() => setSkippedLogin(true)}
      />
    );
  }

  if (!user && !skippedLogin) {
    if (authScreen === 'landing') {
      return (
        <LandingPage
          user={user}
          onSignIn={() => setAuthScreen('login')}
          onSignUp={() => setAuthScreen('signup')}
          onSkip={() => { setSkippedLogin(true); setView('dashboard'); }}
          onPricing={() => setAuthScreen('pricing')}
        />
      );
    }
    if (authScreen === 'pricing') {
      return (
        <PricingPage
          onBack={() => setAuthScreen('landing')}
          onSignUp={() => setAuthScreen('signup')}
          onSignIn={() => setAuthScreen('login')}
        />
      );
    }
    return (
      <>
        <LoginPage
          initialSignUp={authScreen === 'signup'}
          onSkip={() => { setSkippedLogin(true); setView('dashboard'); }}
          onBack={() => setAuthScreen('landing')}
        />
        <GlobalFooterBar />
      </>
    );
  }

  const handleExpenseClick = (expense) => {
    setBulkCatOffer(null);

    if (expense.pairedWithId && expense.pairedWithFile) {
      const pairedFile = savedFiles.find(f => f.name === expense.pairedWithFile);
      const pairedTx = pairedFile?.data?.find(r => r.id === expense.pairedWithId);
      if (pairedTx) {
        const currentIsExpense = (savedFiles.find(f => f.name === currentFileName)?.reportType ?? 'expense') === 'expense';
        const expenseTx = currentIsExpense ? expense  : pairedTx;
        const accountTx = currentIsExpense ? pairedTx : expense;
        // Merge non-amount metadata only; amounts and vendor stay from the clicked record
        const merged = {
          ...expense,
          businessClassification: expenseTx.businessClassification ?? accountTx.businessClassification,
          // Receipt image — prefer whichever side has one
          ...(!expense.imagePreview && pairedTx.imagePreview ? {
            imagePreview: pairedTx.imagePreview,
            mimeType:     pairedTx.mimeType,
            imageId:      pairedTx.imageId,
          } : {}),
        };
        setEditingExpense(merged);
        return;
      }
    }

    setEditingExpense(expense);
  };

  const handleSaveEdit = (updated) => {
    const actor = user?.email ?? null;
    updateExpense(convertExpense({
      ...updated,
      lastModifiedBy: actor,
      lastModifiedAt: new Date().toISOString(),
    }));
    setEditingExpense(null);
    setBulkCatOffer(null);

    // Sync reimbursed flag to the paired transaction item
    if (uid && updated.pairedWithId && updated.pairedWithFile) {
      const pfi = savedFiles.find(f => f.name === updated.pairedWithFile);
      const pairedFileData = pfi?.data ?? bizAllLoaded[updated.pairedWithFile];
      if (pairedFileData) {
        const newPairedData = pairedFileData.map(r =>
          String(r.id) === String(updated.pairedWithId)
            ? { ...r, reimbursed: updated.reimbursed || undefined }
            : r
        );
        saveFile(uid, updated.pairedWithFile, newPairedData, { imageStorage: false, reportType: pfi?.reportType, accountName: pfi?.accountName });
        setBizAllLoaded(prev => ({ ...prev, [updated.pairedWithFile]: newPairedData }));
        patchSavedFile(updated.pairedWithFile, newPairedData);
      }
    }

    // Offer to apply the same category to same-vendor peers in this report
    if (updated.category !== editingExpense.category) {
      const vendor = (updated.vendor || '').trim().toLowerCase();
      const peers = expenses.filter(e =>
        e.id !== updated.id &&
        (e.vendor || '').trim().toLowerCase() === vendor &&
        e.category !== updated.category
      );
      if (peers.length > 0) {
        setBulkCatOffer({
          vendor: updated.vendor,
          category: updated.category,
          peerIds: peers.map(e => e.id),
        });
      }
    }
  };

  const handleApplyBulkCatOffer = () => {
    if (!bulkCatOffer) return;
    const idSet = new Set(bulkCatOffer.peerIds);
    setExpenses(prev => prev.map(e =>
      idSet.has(e.id)
        ? convertExpense({ ...e, category: bulkCatOffer.category })
        : e
    ));
    setBulkCatOffer(null);
  };

  const handleSaveNewExpense = async (formData, targetFileName) => {
    const now = new Date().toISOString();
    const actor = user?.email ?? null;
    const newExpense = convertExpense({
      ...formData,
      id: Date.now(),
      createdBy: actor,
      createdAt: now,
      lastModifiedBy: actor,
      lastModifiedAt: now,
    });
    trackAddExpense(formData.category);
    if (!targetFileName || targetFileName === currentFileName) {
      addExpense(newExpense);
    } else {
      const targetFile = savedFiles.find(f => f.name === targetFileName);
      const targetData = targetFile?.data ?? [];
      await saveFile(uid, targetFileName, [...targetData, newExpense], { imageStorage: limits.imageStorage, reportType: 'expense' });
      await loadSavedFiles();
    }
    setView(addReturnView);
  };

  const handleSavePrefs = async (prefs) => {
    await setPreferences(prefs);
    // Reconvert all existing expenses with the new currency + adjustment factor
    setExpenses(prev => reconvertAllExpenses(prev));
  };

  const handleGenerateMcpToken = async () => {
    await generateMcpToken();
    await refreshAccount();
  };

  const handleRevokeMcpToken = async () => {
    await revokeMcpToken();
    await refreshAccount();
  };

  const handleLoadClick = async () => {
    await loadSavedFiles();
    setShowLoadModal(true);
  };

  const isTxFile = currentFileReportType === 'credit_card' || currentFileReportType === 'bank' || currentFileReportType === 'brokerage';
  const goToAdd = () => {
    if (!currentFileName) { setShowNoReportModal(true); return; }
    if (isTxFile) { alert('Expenses cannot be added to a bank or credit card account. Use an expense report instead.'); return; }
    setAddReturnView(view); setView('add');
  };
  const goToCapture = () => {
    if (!currentFileName) { setShowNoReportModal(true); return; }
    if (isTxFile) { alert('Receipts cannot be scanned into a bank or credit card account. Use an expense report instead.'); return; }
    setAddReturnView(view); setView('capture');
  };

  // When loading an owned file, ensure we clear any shared-file context
  const handleOwnLoad = async (file) => {
    setCurrentFileOwnerUid(null);
    setCurrentFileAccess(null);
    await handleLoad(file);
  };

  const handleNewReport = (name) => {
    setCurrentFileName(name);
    setCurrentFileReportType('expense');
    setCurrentFileOwnerUid(null);
    setCurrentFileAccess(null);
    setExpenses([]);
    setShowNewReportModal(false);
  };

  const handleExportClick = () => {
    setShowExport(true);
  };

  const handleCsvImport = async ({ accountName, transactions, reportType, keepLocal = false }) => {
    if (!uid) return;

    // Ensure account registry entry exists; lazily creates with type:'personal' for legacy accounts.
    const knownAccount = transactionAccounts.find(a => a.name === accountName);
    const account = knownAccount ?? await ensureTransactionAccount(uid, { name: accountName, reportType });
    if (!knownAccount) setTransactionAccounts(prev => [...prev, account]);

    // Auto-generate filename from account + latest transaction month
    const dates = transactions.map(t => t.date).filter(Boolean).sort();
    const ym = dates.length ? dates[dates.length - 1].slice(0, 7) : (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
    const filename = `${accountName} - ${ym}`;

    // Merge with existing file for the same account+month, deduplicating
    const existing = bizAllLoaded[filename] ?? [];
    const toAdd = transactions.filter(tx => !findDuplicate(tx, existing));
    const merged = [...existing, ...toAdd];
    await saveFile(uid, filename, merged, { imageStorage: false, reportType, accountName, type: account.type, local: keepLocal });

    // Pre-populate bizAllLoaded and mark file as initiated so the pre-load effect doesn't
    // overwrite classified data with raw data from Firestore.
    txLoadInitiatedRef.current.add(filename);
    setBizAllLoaded(prev => ({ ...prev, [filename]: merged }));

    const freshFiles = await loadSavedFiles();
    setShowCreditCardImport(false);
    setShowBankImport(false);

    // Auto-classify all transaction files using the freshly loaded file list
    if (freshFiles) {
      try {
        const changes = autoClassifyFiles(freshFiles);
        const batchUpdate = {};
        for (const [name, updatedData] of Object.entries(changes)) {
          const fileInfo = freshFiles.find(f => f.name === name);
          await saveFile(uid, name, updatedData, { imageStorage: false, reportType: fileInfo?.reportType, accountName: fileInfo?.accountName, local: fileInfo?.local });
          patchSavedFile(name, updatedData);
          batchUpdate[name] = updatedData;
        }
        if (Object.keys(batchUpdate).length) {
          setBizAllLoaded(prev => ({ ...prev, ...batchUpdate }));
        }
      } catch (err) {
        console.error('Auto-classify after import failed:', err);
      }
    }
  };

  const handleCreateAccount = async ({ name, reportType, type, openingBalance, openingBalanceDate }) => {
    if (!uid) return;
    await saveTransactionAccount(uid, { name, reportType, type, openingBalance, openingBalanceDate });
    const updated = await listTransactionAccounts(uid);
    setTransactionAccounts(updated);
  };

  const handleRestoreLocal = async (filename, pickedFile) => {
    await handleRestoreLocalFile(filename, pickedFile);
    await loadSavedFiles();
  };

  const handleShareClick = (file) => {
    setSharingFile(file.name);
    setShowShareModal(true);
  };

  const handleSharedChange = () => {
    loadSavedFiles();
  };

  const sidebarProps = {
    user,
    currentFileName,
    autoSaving,
    totalSpend,
    defaultCurrency,
    adjustmentFactor,
    ratesLoading,
    expenses,
    onPrefsClick: () => setShowPrefsModal(true),
    onNewReport: () => {
      const check = checkLimit('maxReports', savedFiles.length);
      if (!check.allowed) { showUpgradePrompt(check); return; }
      setShowNewReportModal(true);
    },
    onLoadClick: handleLoadClick,
    onExportClick: handleExportClick,
    onLogout: logout,
    onLogin: () => setSkippedLogin(false),
    onShareClick: user ? handleShareClick : undefined,
    currentFileAccess,
    tier,
    scansUsed,
    totalScansAllowed,
    isAdmin: account?.isAdmin ?? false,
    onAdminClick: account?.isAdmin ? () => setShowAdminPanel(true) : undefined,
    setAuthScreen: setAuthScreen,
    onBillingClick: user ? () => setShowBillingModal(true) : undefined,
    onReviewLinksClick: user ? () => setShowReviewLinks(true) : undefined,
    onCreateReviewLinkClick: user && currentFileName ? () => setShowCreateReviewLink(true) : undefined,
    onHelpClick: () => setShowHelpModal(true),
    onInstallClick: installPrompt ? async () => {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') setInstallPrompt(null);
    } : undefined,
    onImportCreditCard: user ? () => setShowCreditCardImport(true) : undefined,
    onImportBank: user ? () => setShowBankImport(true) : undefined,
    onImportBrokerage: user ? () => setShowBrokerageImport(true) : undefined,
    onBusinessSummary: user ? () => setView('combined') : undefined,
    onFinancialSummary: user ? () => setView('financial') : undefined,
    onTaxSummary: user ? () => setView('tax') : undefined,
    onBookkeeperClick: user ? () => {
      const check = checkLimit('aiBookkeeper');
      if (!check.allowed) { showUpgradePrompt(check); return; }
      if (bkUsage) {
        const qCheck   = checkLimit('aiQueriesPerWeek',   bkUsage.queriesThisWeek);
        if (!qCheck.allowed)   { showUpgradePrompt(qCheck);   return; }
        const inCheck  = checkLimit('aiTokensInPerWeek',  bkUsage.tokensInThisWeek);
        if (!inCheck.allowed)  { showUpgradePrompt(inCheck);  return; }
        const outCheck = checkLimit('aiTokensOutPerWeek', bkUsage.tokensOutThisWeek);
        if (!outCheck.allowed) { showUpgradePrompt(outCheck); return; }
      }
      setShowBookkeeper(true);
    } : undefined,
  };

  const topBarProps = (({ user, tier, isAdmin, onAdminClick, setAuthScreen,
    onNewReport, onLoadClick, onExportClick, onPrefsClick,
    onCreateReviewLinkClick, onReviewLinksClick, onBillingClick, onInstallClick,
    onHelpClick, onLogout, onLogin, onImportCreditCard, onImportBank,
    onBusinessSummary, onFinancialSummary, onTaxSummary, onBookkeeperClick,
  }) => ({ user, tier, isAdmin, onAdminClick, setAuthScreen,
    onNewReport, onLoadClick, onExportClick, onPrefsClick,
    onCreateReviewLinkClick, onReviewLinksClick, onBillingClick, onInstallClick,
    onHelpClick, onLogout, onLogin, onImportCreditCard, onImportBank,
    onBusinessSummary, onFinancialSummary, onTaxSummary, onBookkeeperClick,
  }))(sidebarProps);

  const goToBSVFiltered = (filters) => {
    setBsvInitialFilters(filters);
    setView('combined');
  };

  const goToTransactionAccount = (filename) => {
    setCurrentTransactionFile(filename);
    setView('transaction_account');
  };

  const financeNavProps = {
    onGoToDashboard: () => setView('dashboard'),
    onGoToFinances:  () => { setBsvInitialFilters(null); setView('combined'); },
    onGoToFinancial: () => setView('financial'),
    onGoToTax:       () => setView('tax'),
    onGoToExpenses:  () => setView('expenses'),
    onGoToAdd:       goToAdd,
    onGoToCapture:   goToCapture,
    onImportCreditCard: () => setShowCreditCardImport(true),
    onImportBank:       () => setShowBankImport(true),
    onImportBrokerage:  () => setShowBrokerageImport(true),
  };

  const bkHandlers = {
    messages:       bkMessages,
    usage:          bkUsage,
    limits:         limits,
    onNewMessage:   (msg)  => setBkMessages(prev => [...prev, msg]),
    onReply:        (data) => {
      if (data.condensedHistory) {
        const [marker, summary, ...rest] = data.condensedHistory;
        setBkMessages([
          ...rest,
          { ...marker, condensed: true },
          { ...summary, condensed: true },
          { role: 'user',      content: data.userMsg },
          { role: 'assistant', content: data.reply },
        ]);
      }
      localStorage.setItem('myspend_gs_chatted', '1');
      setBkUsage(prev => ({
        ...data,
        convTokensIn:  (prev?.convTokensIn  ?? 0) + (data.tokensIn  ?? 0),
        convTokensOut: (prev?.convTokensOut ?? 0) + (data.tokensOut ?? 0),
      }));
    },
    onCondense: (condensedHistory, tokensBefore) => {
      const [marker, summary, ...rest] = condensedHistory;
      setBkMessages([
        ...rest,
        { ...marker, condensed: true },
        { ...summary, condensed: true },
      ]);
      if (tokensBefore != null) {
        setBkUsage(prev => prev ? { ...prev, condenseTokensBefore: tokensBefore, condenseTokensAfter: null } : null);
      }
    },
    onClear: () => {
      setBkMessages([]);
      setBkUsage(prev => prev ? { ...prev, convTokensIn: 0, convTokensOut: 0 } : null);
    },
  };

  const globalModals = (
    <>
      {showNoReportModal && (
        <div
          onClick={() => setShowNoReportModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 20, padding: '32px 28px', maxWidth: 340, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111', marginBottom: 8 }}>No report open</div>
            <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
              Open or create a report before adding expenses.
            </div>
            <button
              onClick={() => { setShowNoReportModal(false); handleLoadClick(); }}
              style={{ background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%', marginBottom: 10 }}
            >
              Open / Create Report
            </button>
            <button
              onClick={() => setShowNoReportModal(false)}
              style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', padding: '6px 12px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {showNewReportModal && (
        <NewReportModal onClose={() => setShowNewReportModal(false)} onCreate={handleNewReport} />
      )}
      {showPrefsModal && (
        <PrefsModal
          defaultCurrency={defaultCurrency}
          adjustmentFactor={adjustmentFactor}
          onSave={handleSavePrefs}
          onClose={() => setShowPrefsModal(false)}
          account={account}
          onGenerateMcpToken={uid ? handleGenerateMcpToken : undefined}
          onRevokeMcpToken={uid ? handleRevokeMcpToken : undefined}
        />
      )}
      {showLoadModal && (
        <LoadModal
          savedFiles={savedFiles}
          sharedWithMeFiles={sharedWithMeFiles}
          onClose={() => setShowLoadModal(false)}
          onLoad={handleOwnLoad}
          onLoadShared={handleLoadShared}
          onDelete={handleDelete}
          onShareClick={handleShareClick}
          onRestoreLocal={handleRestoreLocal}
          user={user}
        />
      )}
      {showShareModal && sharingFile && (
        <ShareModal
          uid={uid}
          userEmail={user?.email}
          filename={sharingFile}
          onClose={() => { setShowShareModal(false); setSharingFile(null); }}
          onSharedChange={handleSharedChange}
          tier={tier}
          tierLimits={limits}
          tierConfig={tierConfig}
          onUpgrade={showUpgradePrompt}
        />
      )}
      {showExport && (
        <ExportModal
          expenses={expenses}
          defaultCurrency={defaultCurrency}
          onClose={() => setShowExport(false)}
          customCategories={customCategories}
          getTaxCategory={getTaxCategory}
          user={user}
          savedFiles={savedFiles}
          currentFileName={currentFileName}
          isShared={!!currentFileOwnerUid || (savedFiles.find(f => f.name === currentFileName)?.isShared ?? false)}
          tierLimits={limits}
          tier={tier}
          tierConfig={tierConfig}
          onUpgrade={showUpgradePrompt}
        />
      )}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
      {showBillingModal && (
        <BillingModal account={account} onClose={() => setShowBillingModal(false)} />
      )}
      {showCreateReviewLink && user && currentFileName && (
        <CreateReviewLinkModal
          user={user}
          fileName={currentFileName}
          expenseCount={expenses.length}
          defaultCurrency={defaultCurrency}
          onClose={() => setShowCreateReviewLink(false)}
        />
      )}
      {showReviewLinks && user && (
        <ReviewLinksModal user={user} onClose={() => setShowReviewLinks(false)} />
      )}
      {showHelpModal && (
        <HelpModal onClose={() => setShowHelpModal(false)} />
      )}
      {showCreditCardImport && (
        <CreditCardImportModal
          onClose={() => setShowCreditCardImport(false)}
          onImport={handleCsvImport}
          existingFiles={savedFiles.filter(f => f.reportType === 'credit_card')}
          registeredAccounts={transactionAccounts.filter(a => a.reportType === 'credit_card')}
        />
      )}
      {showBankImport && (
        <BankImportModal
          onClose={() => setShowBankImport(false)}
          onImport={handleCsvImport}
          existingFiles={savedFiles.filter(f => f.reportType === 'bank')}
          registeredAccounts={transactionAccounts.filter(a => a.reportType === 'bank')}
        />
      )}
      {showBrokerageImport && (
        <BrokerageImportModal
          onClose={() => setShowBrokerageImport(false)}
          onImport={handleCsvImport}
          existingFiles={savedFiles.filter(f => f.reportType === 'brokerage')}
          registeredAccounts={transactionAccounts.filter(a => a.reportType === 'brokerage')}
        />
      )}
      {showCreateAccount && user && (
        <CreateAccountModal
          onClose={() => setShowCreateAccount(false)}
          onSave={handleCreateAccount}
          existingAccounts={transactionAccounts}
        />
      )}
      {showReimbursementModal && user && (
        <ReimbursementModal
          items={reimbursableResult?.detail ?? []}
          grossOwed={reimbursableResult?.grandTotal ?? 0}
          reimbursedTotal={reimbursableResult?.reimbursedTotal ?? 0}
          defaultCurrency={defaultCurrency}
          targetAmount={pendingOwnerTxs.length > 0
            ? parseFloat(pendingOwnerTxs.reduce((s, tx) => s + parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0), 0).toFixed(2))
            : undefined}
          onMark={async (items, value) => {
            const toMark = [...items];
            // First mark=true batch: include pending owner_reimbursement transactions so they clear
            if (value && pendingOwnerTxs.length > 0) {
              toMark.push(...pendingOwnerTxs.map(tx => ({ source_type: 'transaction', filename: tx.filename, id: tx.id })));
              setPendingOwnerTxs([]);
            }
            const patched = await markItemsReimbursed(uid, toMark, value);
            patched.forEach(({ filename, data }) => patchSavedFile(filename, data));
          }}
          onClose={() => { setShowReimbursementModal(false); setPendingOwnerTxs([]); }}
        />
      )}
      <UpgradePrompt promptProps={promptProps} onClose={closeUpgradePrompt} />
      {editingExpense && (
        <EditModal
          expense={editingExpense}
          onSave={handleSaveEdit}
          onClose={() => setEditingExpense(null)}
          categories={allCategories}
          incomeCategories={allIncomeCategories}
          addCategory={uid ? addCategory : null}
          addIncomeCategory={uid ? addIncomeCategory : null}
          savedFiles={savedFiles}
          currentFileName={currentFileName}
          reportType={currentFileReportType}
          onCopyExpense={uid ? handleCopyExpense : null}
          onMoveExpense={uid ? handleMoveExpense : null}
          readOnly={currentFileAccess === 'view'}
        />
      )}
      {showBookkeeper && (
        <BookkeeperPanel
          onClose={() => setShowBookkeeper(false)}
          {...bkHandlers}
        />
      )}
      <GlobalFooterBar />
    </>
  );

  if (view === 'dashboard') {
    return (
      <>
        <DashboardPage
          user={user}
          topBarProps={topBarProps}
          onGoToDashboard={financeNavProps.onGoToDashboard}
          onGoToAdd={goToAdd}
          onGoToCapture={goToCapture}
          onGoToFinancial={financeNavProps.onGoToFinancial}
          onGoToTax={financeNavProps.onGoToTax}
          onGoToExpenses={financeNavProps.onGoToExpenses}
          onGoToFinances={financeNavProps.onGoToFinances}
          onGoToBSVFiltered={goToBSVFiltered}
          onNewReport={sidebarProps.onNewReport}
          onHelpClick={() => setShowHelpModal(true)}
          presetViews={presetViews}
          stats={dashboardStats}
          onBookkeeperClick={sidebarProps.onBookkeeperClick}
          showGettingStarted={!!user && filesLoaded && lastFileLoaded && !dismissedGettingStarted && !gsAllDone}
          onDismissGettingStarted={handleDismissGettingStarted}
          hasExpenses={expenses.length > 0}
          hasImportedTransactions={hasImportedTransactions}
          hasChatted={hasChatted}
          onImportCreditCard={user ? () => setShowCreditCardImport(true) : undefined}
          onImportBank={user ? () => setShowBankImport(true) : undefined}
          onImportBrokerage={user ? () => setShowBrokerageImport(true) : undefined}
          onCreateAccount={user ? () => setShowCreateAccount(true) : undefined}
          onGoToTransactionAccount={user ? goToTransactionAccount : undefined}
          transactionFiles={savedFiles.filter(f => f.reportType === 'credit_card' || f.reportType === 'bank' || f.reportType === 'brokerage')}
          expenseFiles={savedFiles.filter(f => f.reportType === 'expense' || !f.reportType)}
          transactionAccounts={transactionAccounts}
          onReimbursementClick={uid ? (result, pendingPayments = []) => { setReimbursableResult(result); setPendingOwnerTxs(pendingPayments); setShowReimbursementModal(true); } : undefined}
          reportName={currentFileName}
          autoSaving={autoSaving}
          currentFileAccess={currentFileAccess}
          onShareClick={user ? handleShareClick : undefined}
          onLoadClick={handleLoadClick}
          onCreateReviewLinkClick={user && currentFileName ? () => setShowCreateReviewLink(true) : undefined}
          onReconcileClick={user ? () => { setBizOpenReconcile(true); setBsvInitialFilters(null); setView('combined'); } : undefined}
        />
        {globalModals}
      </>
    );
  }

  // Full-screen business summary replaces the entire layout
  if (view === 'combined') {
    return (
      <>
        <BusinessSummaryView
          uid={uid}
          savedFiles={savedFiles}
          defaultCurrency={defaultCurrency}
          categories={allCategories}
          incomeCategories={allIncomeCategories}
          addCategory={uid ? addCategory : null}
          addIncomeCategory={uid ? addIncomeCategory : null}
          canAiReconcile={limits.aiReconcile ?? false}
          allLoaded={bizAllLoaded}
          setAllLoaded={setBizAllLoaded}
          onGoToDashboard={financeNavProps.onGoToDashboard}
          onGoToFinancial={financeNavProps.onGoToFinancial}
          onGoToTax={financeNavProps.onGoToTax}
          onGoToExpenses={financeNavProps.onGoToExpenses}
          onGoToAdd={financeNavProps.onGoToAdd}
          onGoToCapture={financeNavProps.onGoToCapture}
          onImportCreditCard={financeNavProps.onImportCreditCard}
          onImportBank={financeNavProps.onImportBank}
          onImportBrokerage={financeNavProps.onImportBrokerage}
          topBarProps={topBarProps}
          initialFilters={bsvInitialFilters}
          onGoToTransactionAccount={goToTransactionAccount}
          onFilePatch={patchSavedFile}
          initialShowReconcile={bizOpenReconcile}
        />
        {globalModals}
      </>
    );
  }

  if (view === 'transaction_account' && currentTransactionFile) {
    return (
      <>
        <TransactionAccountView
          uid={uid}
          savedFiles={savedFiles}
          filename={currentTransactionFile}
          allLoaded={bizAllLoaded}
          setAllLoaded={setBizAllLoaded}
          defaultCurrency={defaultCurrency}
          categories={allCategories}
          incomeCategories={allIncomeCategories}
          addCategory={uid ? addCategory : null}
          addIncomeCategory={uid ? addIncomeCategory : null}
          onGoToDashboard={financeNavProps.onGoToDashboard}
          onGoToFinances={financeNavProps.onGoToFinances}
          onGoToFinancial={financeNavProps.onGoToFinancial}
          onGoToTax={financeNavProps.onGoToTax}
          onGoToExpenses={financeNavProps.onGoToExpenses}
          onGoToAdd={financeNavProps.onGoToAdd}
          onGoToCapture={financeNavProps.onGoToCapture}
          onImportCreditCard={financeNavProps.onImportCreditCard}
          onImportBank={financeNavProps.onImportBank}
          onImportBrokerage={financeNavProps.onImportBrokerage}
          topBarProps={topBarProps}
          transactionAccounts={transactionAccounts}
          onTransactionAccountsChange={() => listTransactionAccounts(uid).then(setTransactionAccounts)}
          onFilePatch={patchSavedFile}
        />
        {globalModals}
      </>
    );
  }

  if (view === 'financial') {
    return (
      <>
        <FinancialSummaryView
          uid={uid}
          savedFiles={savedFiles}
          defaultCurrency={defaultCurrency}
          onGoToBusiness={(filterKey) => {
            if (filterKey) {
              goToBSVFiltered({
                filterSet: filterKey === 'unclassified_debit'
                  ? ['unclassified', 'unclassified_credit', 'unclassified_debit']
                  : [filterKey],
              });
            } else {
              setBsvInitialFilters(null);
              setView('combined');
            }
          }}
          allLoaded={bizAllLoaded}
          setAllLoaded={setBizAllLoaded}
          onGoToDashboard={financeNavProps.onGoToDashboard}
          onGoToFinances={financeNavProps.onGoToFinances}
          onGoToTax={financeNavProps.onGoToTax}
          onGoToExpenses={financeNavProps.onGoToExpenses}
          onGoToAdd={financeNavProps.onGoToAdd}
          onGoToCapture={financeNavProps.onGoToCapture}
          onImportCreditCard={financeNavProps.onImportCreditCard}
          onImportBank={financeNavProps.onImportBank}
          onImportBrokerage={financeNavProps.onImportBrokerage}
          topBarProps={topBarProps}
        />
        {globalModals}
      </>
    );
  }

  if (view === 'tax') {
    return (
      <>
        <TaxSummaryView
          expenses={expenses}
          savedFiles={savedFiles}
          defaultCurrency={defaultCurrency}
          getTaxCategory={getTaxCategory}
          onGoToDashboard={financeNavProps.onGoToDashboard}
          onGoToFinances={financeNavProps.onGoToFinances}
          onGoToFinancial={financeNavProps.onGoToFinancial}
          onGoToExpenses={financeNavProps.onGoToExpenses}
          onGoToAdd={financeNavProps.onGoToAdd}
          onGoToCapture={financeNavProps.onGoToCapture}
          onImportCreditCard={financeNavProps.onImportCreditCard}
          onImportBank={financeNavProps.onImportBank}
          onImportBrokerage={financeNavProps.onImportBrokerage}
          topBarProps={topBarProps}
        />
        {globalModals}
      </>
    );
  }

  if (view === 'add' || view === 'capture') {
    return (
      <>
        <AddExpenseView
          onSave={handleSaveNewExpense}
          onBack={() => setView(addReturnView)}
          checkLimit={checkLimit}
          showUpgradePrompt={showUpgradePrompt}
          refreshUsage={refreshUsage}
          categories={allCategories}
          addCategory={uid ? addCategory : null}
          reportName={currentFileName}
          currentFileReportType={currentFileReportType}
          savedFiles={savedFiles}
          scansUsed={scansUsed}
          totalScansAllowed={totalScansAllowed}
          tier={tier}
          user={user}
          autoSaving={false}
          currentFileAccess={currentFileAccess}
          onShareClick={user ? handleShareClick : undefined}
          onLoadClick={handleLoadClick}
          onCreateReviewLinkClick={user && currentFileName ? () => setShowCreateReviewLink(true) : undefined}
          totalSpend={totalSpend}
          defaultCurrency={defaultCurrency}
          adjustmentFactor={adjustmentFactor}
          ratesLoading={ratesLoading}
          ratesError={ratesError}
          expenses={expenses}
          autoStartCamera={view === 'capture'}
          {...financeNavProps}
          topBarProps={topBarProps}
        />
        {globalModals}
      </>
    );
  }

  if (view === 'expenses') {
    return (
      <>
        <ExpenseListView
          user={user}
          totalSpend={totalSpend}
          adjustmentFactor={adjustmentFactor}
          expenses={expenses}
          onExpenseClick={handleExpenseClick}
          onDeleteExpense={currentFileAccess === 'view' ? null : deleteExpense}
          defaultCurrency={defaultCurrency}
          isShared={!!currentFileOwnerUid || (savedFiles.find(f => f.name === currentFileName)?.isShared ?? false)}
          reportName={currentFileName}
          currentFileAccess={currentFileAccess}
          onAddExpense={goToAdd}
          ratesLoading={ratesLoading}
          ratesError={ratesError}
          bulkCatOffer={bulkCatOffer}
          onApplyBulkCat={handleApplyBulkCatOffer}
          onDismissBulkCat={() => setBulkCatOffer(null)}
          saveMsg={saveMsg}
          autoSaving={autoSaving}
          onShareClick={user ? handleShareClick : undefined}
          onLoadClick={handleLoadClick}
          onExportClick={handleExportClick}
          onCreateReviewLinkClick={user && currentFileName ? () => setShowCreateReviewLink(true) : undefined}
          tier={tier}
          scansUsed={scansUsed}
          totalScansAllowed={totalScansAllowed}
          reportType={currentFileReportType}
          topBarProps={topBarProps}
          {...financeNavProps}
        />
        {globalModals}
      </>
    );
  }

  return (
    <div
      style={{
        fontFamily: T.fontBody,
        background: T.appBg,
        minHeight: "100vh",
        paddingBottom: 36,
      }}
    >
      <div style={{
        maxWidth: 560,
        width: "100%",
        margin: "0 auto",
        position: "relative",
        overflowX: "hidden",
      }}>

      {view === "home" && (
        <div style={{ paddingBottom: 136 }}>
          <Header
            totalSpend={totalSpend}
            expenses={expenses}
            currentFileName={currentFileName}
            autoSaving={autoSaving}
            defaultCurrency={defaultCurrency}
            adjustmentFactor={adjustmentFactor}
            ratesLoading={ratesLoading}
            ratesError={ratesError}
            onPrefsClick={() => setShowPrefsModal(true)}
            onNewReport={() => {
              const check = checkLimit('maxReports', savedFiles.length);
              if (!check.allowed) { showUpgradePrompt(check); return; }
              setShowNewReportModal(true);
            }}
            onExportClick={handleExportClick}
            onLoadClick={handleLoadClick}
            user={user}
            onLogout={logout}
            onLogin={() => setSkippedLogin(false)}
            onShareClick={user ? handleShareClick : undefined}
            currentFileAccess={currentFileAccess}
            tier={tier}
            scansUsed={scansUsed}
            totalScansAllowed={totalScansAllowed}
            isAdmin={account?.isAdmin ?? false}
            onAdminClick={account?.isAdmin ? () => setShowAdminPanel(true) : undefined}
            setAuthScreen={setAuthScreen}
            onReviewLinksClick={user ? () => setShowReviewLinks(true) : undefined}
            onCreateReviewLinkClick={user && currentFileName ? () => setShowCreateReviewLink(true) : undefined}
            onBillingClick={user ? () => setShowBillingModal(true) : undefined}
            onHelpClick={() => setShowHelpModal(true)}
            onInstallClick={installPrompt ? async () => {
              installPrompt.prompt();
              const { outcome } = await installPrompt.userChoice;
              if (outcome === 'accepted') setInstallPrompt(null);
            } : undefined}
            onImportCreditCard={user ? () => setShowCreditCardImport(true) : undefined}
            onImportBank={user ? () => setShowBankImport(true) : undefined}
            onImportBrokerage={user ? () => setShowBrokerageImport(true) : undefined}
            onBusinessSummary={user ? () => setView('combined') : undefined}
            onFinancialSummary={user ? () => setView('financial') : undefined}
            onTaxSummary={user ? () => setView('tax') : undefined}
            onBookkeeperClick={user ? () => {
              const check = checkLimit('aiBookkeeper');
              if (!check.allowed) { showUpgradePrompt(check); return; }
              if (bkUsage) {
                const qCheck   = checkLimit('aiQueriesPerWeek',   bkUsage.queriesThisWeek);
                if (!qCheck.allowed)   { showUpgradePrompt(qCheck);   return; }
                const inCheck  = checkLimit('aiTokensInPerWeek',  bkUsage.tokensInThisWeek);
                if (!inCheck.allowed)  { showUpgradePrompt(inCheck);  return; }
                const outCheck = checkLimit('aiTokensOutPerWeek', bkUsage.tokensOutThisWeek);
                if (!outCheck.allowed) { showUpgradePrompt(outCheck); return; }
              }
              setShowBookkeeper(true);
            } : undefined}
          />
            {saveMsg && (
              <div
                style={{
                  backgroundColor: "#e6da32ec",
                  padding: "16px 20px",
                  borderRadius: 8,
                }}
              >
                {saveMsg}
              </div>
            )}
          <ExpenseList
            expenses={expenses}
            onExpenseClick={handleExpenseClick}
            onDeleteExpense={currentFileAccess === 'view' ? null : deleteExpense}
            defaultCurrency={defaultCurrency}
            isShared={!!currentFileOwnerUid || (savedFiles.find(f => f.name === currentFileName)?.isShared ?? false)}
            reportName={currentFileName}
          />

          {/* Add Expense Button — hidden for view-only shared reports */}
          {currentFileAccess !== 'view' && (
            <div
              style={{
                position: "fixed",
                bottom: 56,
                right: 20,
                width: 56,
                height: 56,
                borderRadius: 28,
                background: T.gradAmber,
                boxShadow: "0 8px 25px rgba(242, 169, 59, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                color: "#fff",
                cursor: "pointer",
                zIndex: 100,
              }}
              onClick={() => {
                const check = checkLimit('maxExpensesPerReport', expenses.length);
                if (!check.allowed) { showUpgradePrompt(check); return; }
                goToAdd();
              }}
            >
              +
            </div>
          )}

          {/* Bulk category offer — floats above FAB after a category change */}
          {bulkCatOffer && view === "home" && (
            <div style={{
              position: 'fixed', bottom: 120, left: 0, right: 0,
              display: 'flex', justifyContent: 'center',
              zIndex: 99, padding: '0 16px', pointerEvents: 'none',
            }}>
              <div style={{
                background: T.navy, color: '#fff', borderRadius: 14,
                padding: '12px 16px', maxWidth: 440, width: '100%',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                pointerEvents: 'all',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bulkCatOffer.peerIds.length} other {bulkCatOffer.vendor} expense{bulkCatOffer.peerIds.length !== 1 ? 's' : ''} in this report
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Set category to <strong style={{ color: '#fff' }}>{bulkCatOffer.category}</strong>?
                  </div>
                </div>
                <button
                  onClick={handleApplyBulkCatOffer}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: 'none',
                    background: T.gradTeal, color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  Apply to all
                </button>
                <button
                  onClick={() => setBulkCatOffer(null)}
                  style={{
                    padding: '8px 10px', borderRadius: 8,
                    border: '1.5px solid rgba(255,255,255,0.25)',
                    background: 'transparent', color: 'rgba(255,255,255,0.7)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      </div>{/* end scrollable main column */}

      {globalModals}



      {upgradeSuccess && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
          background: 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
          color: '#fff', textAlign: 'center', padding: '14px 20px',
          fontSize: 14, fontWeight: 700, boxShadow: '0 4px 20px rgba(5,196,176,0.4)',
        }}>
          🎉 Payment successful — your plan has been upgraded!
        </div>
      )}
    </div>
  );
}

export default App;
